import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import moment from 'moment';

function Dsrreport() {
  const [fromDate, setFromDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [expenseHeads, setExpenseHeads] = useState({});

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) setUserId(user.uid);
    });
    return () => unsub();
  }, []);

  const fetchReport = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      // 1. Fetch Expense Categories map
      const headsSnap = await getDocs(query(collection(db, "expense_heads"), where("userId", "==", userId)));
      const headsMap = {};
      headsSnap.forEach(d => {
        headsMap[d.id] = d.data().headName;
      });
      setExpenseHeads(headsMap);

      // 2. Fetch Fuel Sales Logs (Daily Ledger)
      const salesSnap = await getDocs(query(
        collection(db, "daily_ledger"),
        where("userId", "==", userId),
        where("date", ">=", fromDate),
        where("date", "<=", toDate),
        orderBy("date", "desc")
      ));
      const salesRecords = salesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // 3. Fetch Stock Purchases Inward
      const purchaseSnap = await getDocs(query(
        collection(db, "purchase_ledger"),
        where("userId", "==", userId),
        where("date", ">=", fromDate),
        where("date", "<=", toDate)
      ));
      const purchaseRecords = purchaseSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // 4. Fetch Daily Operational Expenses
      const expenseSnap = await getDocs(query(
        collection(db, "daily_expenses"),
        where("userId", "==", userId),
        where("date", ">=", fromDate),
        where("date", "<=", toDate)
      ));
      const expenseRecords = expenseSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // 5. Fetch Tally Denominations Breakdown
      const denomSnap = await getDocs(query(
        collection(db, "denominations"),
        where("userId", "==", userId),
        where("date", ">=", fromDate),
        where("date", "<=", toDate)
      ));
      const denomRecords = denomSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // 6. Consolidate by unique dynamic dates
      const allDates = Array.from(new Set(salesRecords.map(s => s.date)));
      allDates.sort((a, b) => b.localeCompare(a));

      const consolidatedDays = allDates.map(currentDate => {
        const daySales = salesRecords.filter(s => s.date === currentDate);
        const dayExpenses = expenseRecords.filter(e => e.date === currentDate);
        const dayDenom = denomRecords.find(d => d.date === currentDate) || null;

        const productBlocks = daySales.map(sale => {
          const matchingPurchase = purchaseRecords.find(p => p.date === currentDate && p.fuelType === sale.fuelType);

          const calculatedOpeningStock = (sale.nozzleSales || []).reduce((sum, nozzle) => {
            const opVal = nozzle && nozzle.op ? parseFloat(nozzle.op) : 0;
            return sum + opVal;
          }, 0);

          return {
            fuelType: sale.fuelType,
            nozzleSales: sale.nozzleSales || [],
            pumpTesting: parseFloat(sale.pumpTesting || 0),
            openingStock: calculatedOpeningStock,

            receipts: matchingPurchase ? parseFloat(matchingPurchase.totalSales || 0) : 0,
            actualDipStock: parseFloat(sale.closingStock || 0)
          };
        });

        return {
          date: currentDate,
          products: productBlocks,
          expenses: dayExpenses,
          denomination: dayDenom
        };
      });

      setReportData(consolidatedDays);
    } catch (error) {
      console.error("Error generating master DSR statement:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  return (
    <div className="content-wrapper bg-white min-vh-100 text-dark">
      {/* FILTER CONTROL HUB */}
      <section className="no-print p-3 bg-light border-bottom border-dark">
        <div className="row align-items-end">
          <div className="col-md-3 col-sm-6">
            <label className="text-xs font-weight-bold text-dark mb-1">From Date</label>
            <input type="date" className="form-control form-control-sm border-dark text-xs" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="col-md-3 col-sm-6">
            <label className="text-xs font-weight-bold text-dark mb-1">To Date</label>
            <input type="date" className="form-control form-control-sm border-dark text-xs" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div className="col-md-5 col-sm-6 mt-2">
            <button className="btn btn-sm btn-dark px-3 mr-2 font-weight-bold shadow-sm" onClick={fetchReport} disabled={loading}>
              {loading ? 'Compiling Ledger Records...' : 'Generate Master DSR Register'}
            </button>
            <button className="btn btn-sm btn-outline-dark font-weight-bold shadow-sm" onClick={handlePrint}>
              <i className="fas fa-print mr-1"></i> Print Landscape Register
            </button>
          </div>
        </div>
      </section>

      {/* RENDER MASTER DSR LAYOUT CONTAINER */}
      <section className="content p-3 report-container bg-white">
        <div className="text-center mb-4 border-bottom border-dark pb-2">
          <h2 className="font-weight-bold mb-0 text-black">SHRIMATI PADMAVATI PETROLEUM</h2>
          <h6 className="text-uppercase tracking-wider mt-1 text-bold text-secondary text-xs">Complete Daily Sales, Inward Stock, Expense &amp; Cash Tally Register</h6>
          <div className="text-xs font-weight-bold mt-1 bg-light d-inline-block px-3 py-1 rounded border border-dark">
            Statement Period: {moment(fromDate).format('DD/MM/YYYY')} to {moment(toDate).format('DD/MM/YYYY')}
          </div>
        </div>

        {reportData.length > 0 ? reportData.map((dayBlock) => {
          const totalDayExpenses = dayBlock.expenses.reduce((acc, curr) => acc + (curr.amount || 0), 0);

          return (
            <div key={dayBlock.date} className="dsr-day-wrapper mb-5 border-double-bottom p-2 page-break-avoid">

              {/* Date Header Segment Banner */}
              <div className="bg-dark text-white p-2 mb-2 rounded-sm d-flex justify-content-between align-items-center font-weight-bold text-xs tracking-wider">
                <span><i className="fas fa-calendar-day mr-2 text-warning"></i>DATE: {moment(dayBlock.date).format('DD MMMM YYYY (dddd)')}</span>
                <span className="text-muted text-xxs-dim">MONTH: {moment(dayBlock.date).format('MMMM')} / YEAR: {moment(dayBlock.date).format('YYYY')}</span>
              </div>

              {/* SECTION A: MAIN PRODUCT SALES LEDGER MATRICES */}
              {dayBlock.products.map((product, pIdx) => {
                // Ensure array loop always evaluates exactly 6 nozzle columns safely
                const targetNozzles = Array.from({ length: 4 }, (_, idx) => {
                  return product.nozzleSales[idx] || { op: 0, cl: 0 };
                });

                const totalMeterSales = targetNozzles.reduce((acc, n) => {
                  const sale = (n.cl && n.op) ? (parseFloat(n.cl) - parseFloat(n.op)) : 0;
                  return acc + (sale > 0 ? sale : 0);
                }, 0);

                const actualSales = totalMeterSales - product.pumpTesting;
                const calculatedClosingStock = product.openingStock + product.receipts - actualSales;
                const variation = product.actualDipStock - calculatedClosingStock;

                return (
                  <div key={`${dayBlock.date}_${pIdx}`} className="ledger-block mb-3 border border-dark bg-white">
                    <div className="bg-light p-1 border-bottom border-dark font-weight-bold text-xs pl-2 text-black text-uppercase">
                      Product Unit: <span className="text-primary font-weight-bolder">{product.fuelType}</span>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-sm table-bordered text-center m-0 dsr-horizontal-table text-black">
                        <thead>
                          <tr className="main-header-row text-xxs font-weight-bold bg-gray-200">
                            {/* <th style={{ width: '3%' }}>दिनांक<br />DATE</th> */}
                            <th style={{ width: '7%' }}>प्रारंभिक स्टॉक<br />OPENING STOCK</th>
                            <th style={{ width: '5%' }}>प्रतियां<br />RECEIPTS</th>
                            <th style={{ width: '8%' }}>कुल<br />TOTAL (2+3)</th>
                            <th style={{ width: '8%' }} className="bg-light-yellow">वास्तविक बिक्री<br />ACTUAL SALES</th>
                            <th style={{ width: '33%' }} className="p-0">
                              <div className="p-1 border-bottom border-dark font-weight-bold text-xxs tracking-wide">पी.एम.पी. बिक्री - NOZZLE PERFORMANCE MATRIX (HORIZONTAL METER READINGS)</div>
                              <div className="d-flex nozzle-header-split font-weight-bold">
                                <span style={{ width: '25%' }} className="border-right border-dark">PUMP 1</span>
                                <span style={{ width: '25%' }} className="border-right border-dark">PUMP 2</span>
                                <span style={{ width: '25%' }} className="border-right border-dark">PUMP 3</span>
                                <span style={{ width: '25%' }} className="border-right border-dark">PUMP 4</span>
                              </div>
                            </th>
                            <th style={{ width: '7%' }}>कुल मीटर बिक्री<br />TOTAL METER SALES</th>
                            <th style={{ width: '5%' }}>पम्प जांच<br />PUMP TESTING</th>
                            <th style={{ width: '7%' }}>अंतिम स्टॉक<br />CALCULATED STOCK</th>
                            {/* <th style={{ width: '7%' }} className="bg-light-green">वास्तविक अंतिम स्टॉक<br />ACTUAL DIP STOCK</th> */}
                            {/* <th style={{ width: '5%' }}>भिन्नता<br />VARIATION</th> */}
                          </tr>
                          <tr className="col-numbers-row text-xxs font-weight-normal text-muted bg-light">
                            <td></td><td></td><td></td><td className="bg-light-yellow"></td>
                            <td className="p-0">
                              <div className="d-flex text-center nozzle-no-split">
                                {[...Array(4)].map((_, i) => (
                                  <span key={i} style={{ width: '25%' }} className={i < 5 ? "border-right border-dark" : ""}>
                                    <div className="d-flex w-100 text-xxs-dim font-weight-bold">
                                      <div style={{ width: '33.3%' }} className="border-right border-light">OP</div>
                                      <div style={{ width: '33.3%' }} className="border-right border-light">CL</div>
                                      <div style={{ width: '33.4%' }} className="text-black font-weight-bold">SL</div>
                                    </div>
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td></td><td></td>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="text-xs align-middle font-weight-medium">
                            {/* <td className="font-weight-bold bg-light-gray">{moment(dayBlock.date).format('DD')}</td> */}
                            <td className="font-weight-bold text-dark">{product.openingStock ? product.openingStock.toFixed(2) : '0.00'}</td>
                            <td>{product.receipts.toFixed(2)}</td>
                            <td className="font-weight-bold">{(product.openingStock + product.receipts).toFixed(2)}</td>
                            <td className="font-weight-bold bg-light-yellow text-sm">{actualSales.toFixed(2)}</td>
                            <td className="p-0 align-middle">
                              <div className="d-flex w-100 h-100 align-items-stretch">
                                {targetNozzles.map((nozzle, idx) => {
                                  const opVal = nozzle?.op ? parseFloat(nozzle.op) : 0;
                                  const clVal = nozzle?.cl ? parseFloat(nozzle.cl) : 0;
                                  const nSale = clVal - opVal;

                                  return (
                                    <div key={idx} style={{ width: '25%' }} className={`d-flex align-items-center justify-content-between text-center p-0 ${idx < 5 ? "border-right border-dark" : ""}`}>
                                      <div style={{ width: '33.3%' }} className="p-1 border-right border-light text-xxs-dim">{opVal > 0 ? opVal.toFixed(0) : '0'}</div>
                                      <div style={{ width: '33.3%' }} className="p-1 border-right border-light text-xxs-dim">{clVal > 0 ? clVal.toFixed(0) : '0'}</div>
                                      <div style={{ width: '33.4%' }} className="p-1 font-weight-bolder text-xxs bg-light-gray text-black">{(nSale > 0) ? nSale.toFixed(1) : '0.0'}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>

                            <td className="font-weight-bold text-dark">{totalMeterSales.toFixed(2)}</td>
                            <td>{product.pumpTesting.toFixed(2)}</td>
                            <td className="bg-light-gray">{calculatedClosingStock.toFixed(2)}</td>
                            {/* <td className="font-weight-bolder bg-light-green text-dark">{product.actualDipStock.toFixed(2)}</td> */}
                            {/* <td className={`font-weight-bolder ${variation < 0 ? 'text-danger' : 'text-success'}`}>
                              {variation.toFixed(2)}
                            </td> */}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}

              {/* SECTION B & C: ALIGNED OPERATIONAL DEBITS & CASH TALLY LEDGER ROWS */}
              <div className="row mt-2 mx-0 breakdown-container">

                {/* ADMINISTRATIVE EXPENSES LEDGER WITH SR NO */}
                <div className="col-12 col-lg-6 p-1">
                  <div className="border border-dark bg-white rounded-sm h-100">
                    <div className="bg-danger text-white font-weight-bold text-xxs p-1 px-2 uppercase-tracking">
                      <i className="fas fa-receipt mr-1"></i> Section B: Daily Cash Outflows / Operational Debits Ledger
                    </div>
                    {dayBlock.expenses.length > 0 ? (
                      <table className="table table-sm text-xxs border-0 m-0 text-center custom-row-ledger">
                        <thead>
                          <tr className="bg-light text-dark font-weight-bold border-bottom border-dark">
                            <th style={{ width: '15%' }}>Sr No.</th>
                            <th style={{ width: '35%' }} className="text-left">Expense Head / Category</th>
                            <th style={{ width: '30%' }} className="text-left">Particulars / Memo Note</th>
                            <th style={{ width: '20%' }} className="text-right">Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dayBlock.expenses.map((exp, index) => (
                            <tr key={exp.id}>
                              <td className="font-weight-bold bg-light-gray">{index + 1}</td>
                              <td className="font-weight-bold text-left text-dark">{expenseHeads[exp.headId] || 'General Debit'}</td>
                              <td className="text-left max-text-truncate-ledger text-muted">{exp.description || '--'}</td>
                              <td className="text-right font-weight-bold text-danger">₹{exp.amount?.toFixed(2)}</td>
                            </tr>
                          ))}
                          <tr className="bg-light-yellow font-weight-bold border-top border-dark text-dark text-xs">
                            <td colSpan="3" className="text-right uppercase-tracking">Total Disbursed Cash Vol:</td>
                            <td className="text-right text-danger font-weight-bolder">₹{totalDayExpenses.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <div className="p-3 text-muted text-center font-italic text-xxs bg-white">No administrative debit adjustments logged on this operational date.</div>
                    )}
                  </div>
                </div>

                {/* CASH DENOMINATIONS STATEMENT WITH SR NO */}
                <div className="col-12 col-lg-6 p-1">
                  <div className="border border-dark bg-white rounded-sm h-100">
                    <div className="bg-navy text-white font-weight-bold text-xxs p-1 px-2 uppercase-tracking">
                      <i className="fas fa-coins mr-1"></i> Section C: Vault Denominations &amp; Payment Tally Register
                    </div>
                    {dayBlock.denomination && dayBlock.denomination.breakdown ? (
                      <div className="p-0">
                        <table className="table table-sm text-xxs border-0 m-0 text-center custom-row-ledger">
                          <thead>
                            <tr className="bg-light text-dark font-weight-bold border-bottom border-dark">
                              <th style={{ width: '15%' }}>Sr No.</th>
                              <th style={{ width: '40%' }} className="text-left">Denomination Value Unit</th>
                              <th style={{ width: '25%' }}>Volume (Pcs)</th>
                              <th style={{ width: '20%' }} className="text-right">Subtotal (₹)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(dayBlock.denomination.breakdown)
                              .filter(([_, value]) => Number(value) > 0)
                              .map(([channel, value], dIdx) => {
                                const numericCheck = !isNaN(parseFloat(channel));
                                const subtotalVal = numericCheck ? (parseFloat(channel) * value) : parseFloat(value);
                                return (
                                  <tr key={channel}>
                                    <td className="font-weight-bold bg-light-gray">{dIdx + 1}</td>
                                    <td className="font-weight-bold text-left text-dark">
                                      {numericCheck ? `₹${channel} Currency Notes` : `${channel} Receipt Channel`}
                                    </td>
                                    <td>{numericCheck ? `${value} Pcs` : '1 Unit'}</td>
                                    <td className="text-right font-weight-bold text-dark">₹{subtotalVal.toFixed(2)}</td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>

                        {/* Summary Block Balance Fields */}
                        <div className="row text-center border-top border-dark pt-1 pb-1 mx-0 bg-light-gray font-weight-bold text-xxs uppercase-tracking text-dark">
                          <div className="col-4 border-right border-secondary">
                            <span className="text-muted d-block text-xxs-dim">Expected Val</span>
                            <span className="text-dark">₹{dayBlock.denomination.expectedCash?.toFixed(2)}</span>
                          </div>
                          <div className="col-4 border-right border-secondary">
                            <span className="text-muted d-block text-xxs-dim">Collected Vault</span>
                            <span className="text-primary">₹{dayBlock.denomination.actualCash?.toFixed(2)}</span>
                          </div>
                          <div className="col-4">
                            <span className="text-muted d-block text-xxs-dim">Variance Gap</span>
                            <span className={dayBlock.denomination.difference < 0 ? 'text-danger font-weight-bolder' : 'text-success font-weight-bolder'}>
                              ₹{dayBlock.denomination.difference?.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 text-muted text-center font-italic text-xxs bg-white">No custom structural denomination breakdowns logged on this operational date.</div>
                    )}
                  </div>
                </div>

              </div>

            </div>
          );
        }) : (
          <div className="text-center py-5 text-dark font-italic text-xs border border-dashed rounded mt-3 no-print">
            No integrated DSR records mapped within specified parameters. Adjust inputs and re-query.
          </div>
        )}
      </section>

      {/* STYLED CUSTOM REGISTER PRESENTATION LAYER ARCHITECTURE */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .text-black { color: #000000 !important; }
        .bg-navy { background-color: #0f172a !important; }
        .text-xs { font-size: 0.82rem !important; line-height: 1.2; }
        .text-xxs { font-size: 0.68rem !important; line-height: 1.2; }
        .text-xxs-dim { font-size: 0.6rem !important; color: #444; }
        .font-weight-bolder { font-weight: 800 !important; }
        .font-weight-medium { font-weight: 500 !important; }
        .uppercase-tracking { letter-spacing: 0.5px; text-transform: uppercase; }
        .border-double-bottom { border-bottom: 3px double #000 !important; padding-bottom: 20px !important; }
        
        .bg-light-yellow { background-color: #ffffe6 !important; }
        .bg-light-green { background-color: #e6ffe6 !important; }
        .bg-light-gray { background-color: #f5f5f5 !important; }
        
        .dsr-horizontal-table { border: 1.5px solid #000 !important; table-layout: fixed; width: 100%; }
        .dsr-horizontal-table th, .dsr-horizontal-table td { 
          border: 1px solid #000 !important; 
          padding: 3px 2px !important;
          vertical-align: middle;
          text-align: center;
        }

        .main-header-row th { line-height: 1.1; font-weight: 800; }
        .col-numbers-row td { border-top: 0 !important; padding: 1px !important; color: #666; }

        .nozzle-header-split span { display: inline-block; height: 100%; padding: 2px 0; }
        .nozzle-no-split span { display: inline-block; height: 100%; font-weight: normal; padding: 1px 0; }
        .border-right-border-light { border-right: 1px solid #ddd !important; }

        /* Row-Based Ledgers For Expenses and Denominations */
        .custom-row-ledger { width: 100%; border-collapse: collapse; }
        .custom-row-ledger th { padding: 4px 6px !important; background-color: #f8fafc; border: 0 !important; }
        .custom-row-ledger td { padding: 4px 6px !important; border: 0 !important; border-bottom: 1px dashed #cbd5e1 !important; vertical-align: middle; }
        .custom-row-ledger tr:last-child td { border-bottom: 0 !important; }
        
        .max-text-truncate-ledger {
          max-width: 170px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .page-break-avoid { page-break-inside: avoid; break-inside: avoid; }

        @media print {
          .no-print { display: none !important; }
          .report-container { width: 100% !important; padding: 0 !important; margin: 0 !important; }
          .content-wrapper { background: white !important; padding: 0 !important; }
          .dsr-day-wrapper { margin-bottom: 30px !important; border-bottom: 2px dashed #000 !important; }
          .ledger-block { border: 1.5px solid #000 !important; }
          .breakdown-container { display: flex !important; flex-direction: row !important; width: 100% !important; }
          .breakdown-container .col-12 { width: 50% !important; float: left !important; }
          @page { size: landscape; margin: 0.2in; }
          body { background: white !important; color: black !important; }
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      `}} />
    </div>
  );
}

export default Dsrreport;